import React, { useState } from "react";
import {
  Card,
  Table,
  Space,
  Tag,
  Button,
  Typography,
  Alert,
  Modal,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { QueueCounts, WorkerStatus } from "api/Reviews";
import {
  QUEUE_DISPLAY_NAMES,
  BUTTON_CONFIG,
  TAG_CONFIG,
  TEXT_CONFIG,
} from "../config";
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  ScheduleOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

interface QueuesCardProps {
  queues: Record<string, QueueCounts>;
  workers: WorkerStatus[];
  workersError: string | null;
  loading?: boolean;
  autoRefresh?: boolean;
  lastUpdate?: Date | null;
  onAutoRefreshToggle?: () => void;
  onRefresh?: () => void;
  onClearQueueArchive?: (queueName: string) => Promise<void>;
  onClearQueueWaiting?: (queueName: string) => Promise<void>;
  onPause?: (name: string) => Promise<void>;
  onResume?: (name: string) => Promise<void>;
}

type QueueRow = {
  key: string;
  name: string;
  queueName: string;
  waiting: number;
  delayed: number;
  active: number;
  completed: number;
  failed: number;
  total: number;
  paused: boolean | undefined;
};

export const QueuesCard: React.FC<QueuesCardProps> = ({
  queues,
  workers,
  workersError,
  loading,
  autoRefresh,
  lastUpdate,
  onAutoRefreshToggle,
  onRefresh,
  onClearQueueArchive,
  onClearQueueWaiting,
  onPause,
  onResume,
}) => {
  const [actioning, setActioning] = useState<string | null>(null);
  const [clearingArchive, setClearingArchive] = useState<string | null>(null);
  const [clearingWaiting, setClearingWaiting] = useState<string | null>(null);

  const workersByName = new Map(workers.map((w) => [w.name, w]));

  const dataSource: QueueRow[] = Object.entries(queues).map(
    ([queueName, counts]) => {
      const worker = workersByName.get(queueName);
      return {
        key: queueName,
        name: QUEUE_DISPLAY_NAMES[queueName] || queueName,
        queueName,
        waiting: counts.waiting,
        delayed: counts.delayed ?? 0,
        active: counts.active,
        completed: counts.completed,
        failed: counts.failed,
        total:
          counts.waiting +
          (counts.delayed ?? 0) +
          counts.active +
          counts.completed +
          counts.failed,
        paused: worker?.paused,
      };
    }
  );

  const handlePause = (name: string) => {
    if (!onPause) return;
    setActioning(name);
    onPause(name).finally(() => setActioning(null));
  };

  const handleResume = (name: string) => {
    if (!onResume) return;
    setActioning(name);
    onResume(name).finally(() => setActioning(null));
  };

  const handleClearArchive = (queueName: string, displayName: string) => {
    Modal.confirm({
      title: "Очистить архив?",
      content: `Будут удалены все завершённые и ошибочные задачи в очереди «${displayName}». Продолжить?`,
      okText: "Очистить",
      cancelText: "Отмена",
      onOk: async () => {
        if (!onClearQueueArchive) return;
        setClearingArchive(queueName);
        try {
          await onClearQueueArchive(queueName);
        } finally {
          setClearingArchive(null);
        }
      },
    });
  };

  const handleClearWaiting = (queueName: string, displayName: string) => {
    Modal.confirm({
      title: "Очистить ожидающие?",
      content: `Все задачи в очереди «${displayName}», ещё не взятые в работу, будут удалены. Продолжить?`,
      okText: "Очистить",
      cancelText: "Отмена",
      okButtonProps: { danger: true },
      onOk: async () => {
        if (!onClearQueueWaiting) return;
        setClearingWaiting(queueName);
        try {
          await onClearQueueWaiting(queueName);
        } finally {
          setClearingWaiting(null);
        }
      },
    });
  };

  const columns: ColumnsType<QueueRow> = [
    {
      title: "Очередь / Воркер",
      dataIndex: "name",
      key: "name",
    },
    {
      title: (
        <span>
          <ClockCircleOutlined style={{ marginRight: 4 }} />
          Ожидают
        </span>
      ),
      dataIndex: "waiting",
      key: "waiting",
      width: 90,
      align: "right",
    },
    {
      title: (
        <span>
          <ScheduleOutlined style={{ marginRight: 4 }} />
          Отложены
        </span>
      ),
      dataIndex: "delayed",
      key: "delayed",
      width: 90,
      align: "right",
    },
    {
      title: (
        <span>
          <ReloadOutlined style={{ marginRight: 4 }} />
          Активные
        </span>
      ),
      dataIndex: "active",
      key: "active",
      width: 90,
      align: "right",
    },
    {
      title: (
        <span>
          <CheckCircleOutlined style={{ marginRight: 4 }} />
          Завершены
        </span>
      ),
      dataIndex: "completed",
      key: "completed",
      width: 90,
      align: "right",
    },
    {
      title: (
        <span>
          <ExclamationCircleOutlined style={{ marginRight: 4 }} />
          Ошибки
        </span>
      ),
      dataIndex: "failed",
      key: "failed",
      width: 90,
      align: "right",
    },
    {
      title: "Всего",
      dataIndex: "total",
      key: "total",
      width: 80,
      align: "right",
    },
    {
      title: "Статус воркера",
      dataIndex: "paused",
      key: "paused",
      width: 120,
      render: (paused: boolean | undefined) => {
        if (paused === undefined) return "—";
        return paused ? (
          <Tag color="orange">Остановлен</Tag>
        ) : (
          <Tag color="green">Работает</Tag>
        );
      },
    },
    {
      title: "Действия",
      key: "actions",
      width: 320,
      render: (_: unknown, record: QueueRow) => (
        <Space wrap>
          {onClearQueueArchive && (
            <Button
              size="small"
              icon={<BUTTON_CONFIG.clearArchive.icon />}
              onClick={() => handleClearArchive(record.queueName, record.name)}
              loading={clearingArchive === record.queueName}
            >
              {BUTTON_CONFIG.clearArchive.text}
            </Button>
          )}
          {onClearQueueWaiting && (
            <Button
              size="small"
              danger
              onClick={() => handleClearWaiting(record.queueName, record.name)}
              loading={clearingWaiting === record.queueName}
            >
              {BUTTON_CONFIG.clearWaiting.text}
            </Button>
          )}
          {record.paused === undefined ? (
            "—"
          ) : record.paused ? (
            <Button
              type="primary"
              size="small"
              icon={<PlayCircleOutlined />}
              onClick={() => handleResume(record.queueName)}
              loading={actioning === record.queueName}
            >
              Запустить
            </Button>
          ) : (
            <Button
              size="small"
              icon={<PauseCircleOutlined />}
              onClick={() => handlePause(record.queueName)}
              loading={actioning === record.queueName}
            >
              Остановить
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Очереди и воркеры"
      style={{ marginTop: 16 }}
      extra={
        onRefresh != null && onAutoRefreshToggle != null ? (
          <Space>
            {lastUpdate != null && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {TEXT_CONFIG.lastUpdate} {lastUpdate.toLocaleTimeString()}
              </Text>
            )}
            <Tag
              color={
                autoRefresh
                  ? TAG_CONFIG.autoRefresh.enabled.color
                  : TAG_CONFIG.autoRefresh.disabled.color
              }
              icon={<TAG_CONFIG.autoRefresh.enabled.icon />}
            >
              {autoRefresh
                ? TAG_CONFIG.autoRefresh.enabled.text
                : TAG_CONFIG.autoRefresh.disabled.text}
            </Tag>
            <Button
              size="small"
              type={autoRefresh ? "primary" : "default"}
              onClick={onAutoRefreshToggle}
            >
              {autoRefresh
                ? BUTTON_CONFIG.autoRefresh.disableText
                : BUTTON_CONFIG.autoRefresh.enableText}
            </Button>
            <Button
              size="small"
              icon={<BUTTON_CONFIG.refresh.icon />}
              onClick={onRefresh}
              loading={loading}
            >
              {BUTTON_CONFIG.refresh.text}
            </Button>
          </Space>
        ) : null
      }
    >
      {workersError && (
        <Alert
          type="warning"
          message={workersError}
          style={{ marginBottom: 12 }}
          showIcon
        />
      )}
      <Table<QueueRow>
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        size="small"
        loading={loading}
        locale={{ emptyText: "Нет данных" }}
      />
    </Card>
  );
};
